import {
  NativeAppleRemindersList,
  NativeAppleReminder,
  DueDateComponents,
  TaskPriority,
} from '@demind-inc/core';

export type NativeAppleReminderList = NativeAppleRemindersList;
export type { NativeAppleReminder };

const isNSNotFound = (n?: number) => typeof n === 'number' && n > 100000;

export const isAppleDateOnlyDue = (dueComps?: DueDateComponents): boolean => {
  if (!dueComps) return false;
  const hasYMD =
    dueComps.year !== undefined || dueComps.month !== undefined || dueComps.day !== undefined;
  const hUndef = dueComps.hour === undefined || isNSNotFound(dueComps.hour);
  const mUndef = dueComps.minute === undefined || isNSNotFound(dueComps.minute);
  return !!hasYMD && hUndef && mUndef;
};

interface PreviousAppleRemindersSyncState {
  reminderIds: string[];
  allowedListIds: string[];
  lastSyncTime: string;
}

const getSyncStateKey = (userId: string) => `apple_reminders_sync_${userId}`;

export const loadPreviousSyncState = (userId: string): PreviousAppleRemindersSyncState | null => {
  try {
    const raw = localStorage.getItem(getSyncStateKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveSyncState = (userId: string, state: PreviousAppleRemindersSyncState): void => {
  try {
    localStorage.setItem(getSyncStateKey(userId), JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
};

export const computeDeletedReminderIds = (
  previousState: PreviousAppleRemindersSyncState | null,
  currentReminderIds: string[],
  currentAllowedListIds: string[]
): string[] => {
  if (!previousState || previousState.reminderIds.length === 0) return [];

  const prevListKey = (previousState.allowedListIds ?? []).slice().sort().join(',');
  const currListKey = currentAllowedListIds.slice().sort().join(',');
  if (prevListKey !== currListKey) return [];

  const currentSet = new Set(currentReminderIds);
  return previousState.reminderIds.filter((id) => !currentSet.has(id));
};

export const sanitizeRemindersForSync = (reminders: NativeAppleReminder[]): NativeAppleReminder[] =>
  reminders.map((r) =>
    isAppleDateOnlyDue(r.dueDateComponents) ? { ...r, dueDate: undefined } : r
  );

export type AppleRecurrenceRule = {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval?: number;
  occurrence?: number;
  endDate?: string;
  daysOfWeek?: Array<'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA'>;
};

const TEXT_TO_WEEKDAY_CODE: Record<string, 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA'> = {
  sun: 'SU', sunday: 'SU',
  mon: 'MO', monday: 'MO',
  tue: 'TU', tuesday: 'TU',
  wed: 'WE', wednesday: 'WE',
  thu: 'TH', thursday: 'TH',
  fri: 'FR', friday: 'FR',
  sat: 'SA', saturday: 'SA',
};

export const mapTaskPriorityToApplePriority = (priority?: TaskPriority | null): number => {
  switch (priority) {
    case 'high':
      return 1;
    case 'medium':
      return 5;
    case 'low':
      return 9;
    default:
      return 0;
  }
};

export const recurrenceStringToAppleRule = (
  recurrenceString: string | undefined | null
): AppleRecurrenceRule | null => {
  if (!recurrenceString) return null;
  const lower = recurrenceString.trim().toLowerCase();

  if (lower === 'every day') {
    return { frequency: 'daily', interval: 1 };
  }

  if (lower === 'every weekday') {
    return { frequency: 'weekly', interval: 1, daysOfWeek: ['MO', 'TU', 'WE', 'TH', 'FR'] };
  }

  const dayMatch = lower.match(
    /^every\s+(sun|mon|tue|wed|thu|fri|sat|sunday|monday|tuesday|wednesday|thursday|friday|saturday)$/
  );
  if (dayMatch) {
    const dayCode = TEXT_TO_WEEKDAY_CODE[dayMatch[1]];
    if (dayCode) {
      return { frequency: 'weekly', interval: 1, daysOfWeek: [dayCode] };
    }
  }

  const yearlyMatch = lower.match(
    /^every\s+(\d{1,2})(?:st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)$/
  );
  if (yearlyMatch) {
    return { frequency: 'yearly', interval: 1 };
  }

  const monthlyMatch = lower.match(/^every\s+(\d{1,2})(?:st|nd|rd|th)?$/);
  if (monthlyMatch) {
    return { frequency: 'monthly', interval: 1 };
  }

  return null;
};

export interface AppleRemindersBridgeResponse {
  ok: boolean;
  error?: string;
  authorizationStatus?: string;
  lists?: NativeAppleReminderList[];
  reminders?: NativeAppleReminder[];
}

export interface AppleReminderMutationBridgeResponse {
  ok: boolean;
  error?: string;
  authorizationStatus?: string;
  reminder?: NativeAppleReminder | null;
}

export interface AppleReminderDeleteBridgeResponse {
  ok: boolean;
  error?: string;
  authorizationStatus?: string;
  deleted?: boolean;
}

export interface DesktopCreateAppleReminderPayload {
  title: string;
  notes?: string;
  dueDate?: string;
  allDay?: boolean;
  priority?: number;
  listIdentifier?: string;
  recurrenceRule?: AppleRecurrenceRule;
}

export interface DesktopUpdateAppleReminderPayload extends DesktopCreateAppleReminderPayload {
  reminderId: string;
  completed?: boolean;
  clearDueDate?: boolean;
  clearRecurrenceRule?: boolean;
}

interface DesktopDeleteAppleReminderPayload {
  reminderId: string;
}

interface DesktopCompleteAppleReminderPayload {
  reminderId: string;
  completed: boolean;
}

interface DesktopBridge {
  platform?: string;
  getAppleReminders?: () => Promise<AppleRemindersBridgeResponse>;
  createAppleReminder?: (
    payload: DesktopCreateAppleReminderPayload
  ) => Promise<AppleReminderMutationBridgeResponse>;
  updateAppleReminder?: (
    payload: DesktopUpdateAppleReminderPayload
  ) => Promise<AppleReminderMutationBridgeResponse>;
  deleteAppleReminder?: (
    payload: DesktopDeleteAppleReminderPayload
  ) => Promise<AppleReminderDeleteBridgeResponse>;
  completeAppleReminder?: (
    payload: DesktopCompleteAppleReminderPayload
  ) => Promise<AppleReminderMutationBridgeResponse>;
  openAppleRemindersSettings?: () => Promise<boolean>;
}

const getDesktopBridge = (): DesktopBridge | undefined => {
  return (window as unknown as { lifestack?: DesktopBridge }).lifestack;
};

const isBridgeHandlerMissing = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('no handler registered') ||
    normalized.includes('bridge unavailable') ||
    normalized.includes('undefined is not a function')
  );
};

const normalizeAppleRemindersBridgeError = (error: unknown): Error => {
  const rawMessage = error instanceof Error ? error.message : String(error);

  if (
    rawMessage.includes('reminder_permission_denied') ||
    rawMessage.includes('reminders_permission_denied')
  ) {
    return new Error(
      'Apple Reminders permission denied. Enable Reminders access in macOS Settings and try again.'
    );
  }

  if (rawMessage.includes('reminders_bridge_missing')) {
    return new Error(
      'Apple Reminders native bridge is missing. Run `yarn --cwd desktop native:build:reminders`.'
    );
  }

  if (isBridgeHandlerMissing(rawMessage)) {
    return new Error('Apple Reminders desktop bridge is unavailable. Restart the desktop app.');
  }

  return new Error(rawMessage || 'Apple Reminders desktop bridge request failed.');
};

const ensureBridgeMethod = <T extends keyof DesktopBridge>(
  methodName: T
): NonNullable<DesktopBridge[T]> => {
  const desktopBridge = getDesktopBridge();
  const bridgeMethod = desktopBridge?.[methodName];

  if (desktopBridge?.platform !== 'darwin' || typeof bridgeMethod !== 'function') {
    throw new Error('Apple Reminders bridge unavailable in this environment.');
  }

  return bridgeMethod as NonNullable<DesktopBridge[T]>;
};

export const isAppleRemindersDesktopSupported = (): boolean => {
  const desktopBridge = getDesktopBridge();
  return (
    desktopBridge?.platform === 'darwin' && typeof desktopBridge?.getAppleReminders === 'function'
  );
};

export const fetchAppleRemindersFromDesktop = async (): Promise<{
  lists: NativeAppleReminderList[];
  reminders: NativeAppleReminder[];
}> => {
  try {
    const getAppleReminders = ensureBridgeMethod('getAppleReminders');
    const response = await getAppleReminders();

    if (!response?.ok) {
      const errorMessage = response?.error || "Couldn't load reminders from the desktop app.";
      const statusSuffix = response?.authorizationStatus
        ? ` (status: ${response.authorizationStatus})`
        : '';
      throw new Error(`${errorMessage}${statusSuffix}`);
    }

    return {
      lists: response.lists ?? [],
      reminders: response.reminders ?? [],
    };
  } catch (error) {
    throw normalizeAppleRemindersBridgeError(error);
  }
};

export const createAppleReminderDesktop = async (
  payload: DesktopCreateAppleReminderPayload
): Promise<NativeAppleReminder | null> => {
  try {
    const createAppleReminder = ensureBridgeMethod('createAppleReminder');
    const response = await createAppleReminder(payload);

    if (!response?.ok || !response?.reminder) {
      throw new Error(response?.error || "Couldn't create that reminder.");
    }

    return response.reminder;
  } catch (error) {
    throw normalizeAppleRemindersBridgeError(error);
  }
};

export const updateAppleReminderDesktop = async (
  payload: DesktopUpdateAppleReminderPayload
): Promise<NativeAppleReminder | null> => {
  try {
    const updateAppleReminder = ensureBridgeMethod('updateAppleReminder');
    const response = await updateAppleReminder(payload);

    if (!response?.ok || !response?.reminder) {
      throw new Error(response?.error || "Couldn't update that reminder.");
    }

    return response.reminder;
  } catch (error) {
    throw normalizeAppleRemindersBridgeError(error);
  }
};

export const deleteAppleReminderDesktop = async (reminderId: string): Promise<boolean> => {
  try {
    const deleteAppleReminder = ensureBridgeMethod('deleteAppleReminder');
    const response = await deleteAppleReminder({ reminderId });

    if (!response?.ok && response?.error !== 'reminder_not_found') {
      throw new Error(response?.error || "Couldn't delete that reminder.");
    }

    return !!response.deleted;
  } catch (error) {
    const normalized = normalizeAppleRemindersBridgeError(error);
    if (normalized.message?.includes('reminder_not_found')) {
      return true;
    }
    throw normalized;
  }
};

export const completeAppleReminderDesktop = async (
  reminderId: string,
  completed: boolean
): Promise<NativeAppleReminder | null> => {
  try {
    const completeAppleReminder = ensureBridgeMethod('completeAppleReminder');
    const response = await completeAppleReminder({ reminderId, completed });

    if (!response?.ok || !response?.reminder) {
      throw new Error(response?.error || "Couldn't update that reminder's status.");
    }

    return response.reminder;
  } catch (error) {
    throw normalizeAppleRemindersBridgeError(error);
  }
};

export const openAppleRemindersSettings = async (): Promise<boolean> => {
  const desktopBridge = getDesktopBridge();
  if (typeof desktopBridge?.openAppleRemindersSettings !== 'function') {
    return false;
  }

  try {
    return await desktopBridge.openAppleRemindersSettings();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (isBridgeHandlerMissing(message)) {
      return false;
    }
    throw normalizeAppleRemindersBridgeError(error);
  }
};
